import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { TrendsType } from "reedy-data/models";
import entries from "lodash/entries";
import { Button } from "~/components/button";
import { ITrendWithProducts, useTrends } from "~/contexts/trends-context";
import type { Table } from "~/shared/react-table/app-table";
import { useAnalytics } from "~/contexts/analytics-context";
import { useSession } from "~/contexts/auth-context";
import { CountrySelect } from "./country-select";
import { SearchTermsSourceSelect } from "~/pages/search-terms/components/search-terms-source-select";
import { SearchTermsCountryToggle } from "~/pages/search-terms/components/search-terms-country-toggle";

interface PreHeaderProps {
  table: Table<ITrendWithProducts>;
  isEmptyAmazon: boolean;
}

export const PreHeader = ({ table, isEmptyAmazon }: PreHeaderProps) => {
  const { t } = useTranslation(["searchTerms"]);
  const navigate = useNavigate();
  const [navigateToSelected, setNavigateToSelected] = useState(false);

  const {
    setIndexCurrentTrendWithProducts,
    setSelectedTrendsWithProducts,
    selectedTrendsType,
    trendsWithProducts,
    trendTypes,
    setSelectedTrendsType,
    top3CountryCodes,
    selectedCountryCode,
    setSelectedCountryCode,
  } = useTrends();

  const { organisation } = useSession();

  const { rowSelection } = table.state;

  const selectedIds = useMemo(() => {
    const selected = entries(rowSelection).filter(([, checked]) => checked);

    return selected.map(([id]) => id);
  }, [rowSelection]);

  const { gaEvent } = useAnalytics();

  const handleTabTrendsSeeBooks = useCallback(() => {
    const trends = trendsWithProducts.filter(
      (trend) => trend.id && selectedIds.includes(trend.id),
    );

    gaEvent({
      type: "see_trends_books",
      payload: {
        terms_count: trends.length,
        trend_type: selectedTrendsType,
      },
    });
    setSelectedTrendsWithProducts(trends);
    setIndexCurrentTrendWithProducts(0);
    setNavigateToSelected(true);
  }, [
    setIndexCurrentTrendWithProducts,
    setSelectedTrendsWithProducts,
    selectedIds,
    trendsWithProducts,
    gaEvent,
    selectedTrendsType,
  ]);

  useEffect(() => {
    if (navigateToSelected) {
      navigate({ to: "/search-terms/selected-books" });
      // resets the one-shot navigation flag after performing the post-selection navigation; the navigation must run after the selected-trends state has committed
      setNavigateToSelected(false);
    }
  }, [navigate, navigateToSelected]);

  const handleSourceChange = useCallback(
    (trend: TrendsType) => {
      if (
        trend === TrendsType.AMAZON &&
        organisation.settings?.canSeeAmazonSearchTerms
      ) {
        navigate({ to: "/search-terms/amazon" });
        return;
      }

      setSelectedTrendsType(trend);
    },
    [navigate, setSelectedTrendsType, organisation],
  );

  return (
    <div className="flex flex-row items-center justify-between w-full">
      <div className="flex flex-col sm:flex-row items-center flex-start gap-2">
        <SearchTermsSourceSelect
          trends={trendTypes}
          onChange={handleSourceChange}
          value={selectedTrendsType}
        />

        {selectedTrendsType === TrendsType.GOOGLE && (
          <>
            <div className="flex-row items-center flex-start gap-2 hidden lg:flex">
              <SearchTermsCountryToggle
                countryCodes={top3CountryCodes}
                value={selectedCountryCode}
                onChange={setSelectedCountryCode}
              />
            </div>
            <CountrySelect />
          </>
        )}
      </div>
      {!isEmptyAmazon ? (
        <Button
          variant="primary"
          label={t("searchTerms:searchTermsList.buttons.seeBooks")}
          icon="arrow-right"
          iconPosition="right"
          onClick={handleTabTrendsSeeBooks}
          disabled={!selectedIds.length}
          analyticsId="button_see_books"
          testId="see-books-button"
        />
      ) : null}
    </div>
  );
};
